import type { PaddleState } from '../../types/game';

export interface PaddleProps {
  paddle: PaddleState;
  context: CanvasRenderingContext2D;
  color?: string;
  strokeColor?: string;
  strokeWidth?: number;
  rounded?: boolean;
  roundedRadius?: number;
}

/**
 * Paddle atomic component - renders a pong paddle on canvas
 */
export class Paddle {
  private props: PaddleProps;

  constructor(props: PaddleProps) {
    this.props = props;
  }

  /**
   * Update paddle properties
   */
  public updateProps(props: Partial<PaddleProps>): void {
    this.props = { ...this.props, ...props };
  }

  /**
   * Render the paddle on canvas
   */
  public render(): void {
    const { 
      paddle, 
      context, 
      color = '#ffffff', 
      strokeColor = '#ffffff', 
      strokeWidth = 0,
      rounded = false,
      roundedRadius = 4
    } = this.props;
    
    context.save();
    
    // Set paddle color
    context.fillStyle = color;
    
    if (rounded) {
      // Draw rounded rectangle
      this.drawRoundedRect(
        context,
        paddle.position.x,
        paddle.position.y,
        paddle.size.width,
        paddle.size.height,
        roundedRadius
      );
    } else {
      // Draw regular rectangle
      context.fillRect(
        paddle.position.x,
        paddle.position.y,
        paddle.size.width,
        paddle.size.height
      );
    }
    
    // Draw stroke if specified
    if (strokeWidth > 0) {
      context.lineWidth = strokeWidth;
      context.strokeStyle = strokeColor;
      
      if (rounded) {
        this.strokeRoundedRect(
          context,
          paddle.position.x,
          paddle.position.y,
          paddle.size.width,
          paddle.size.height,
          roundedRadius
        );
      } else {
        context.strokeRect(
          paddle.position.x,
          paddle.position.y,
          paddle.size.width,
          paddle.size.height
        );
      }
    }
    
    context.restore();
  }

  /**
   * Draw a rounded rectangle
   */
  private drawRoundedRect(
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ): void {
    context.beginPath();
    context.moveTo(x + radius, y);
    context.lineTo(x + width - radius, y);
    context.quadraticCurveTo(x + width, y, x + width, y + radius);
    context.lineTo(x + width, y + height - radius);
    context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    context.lineTo(x + radius, y + height);
    context.quadraticCurveTo(x, y + height, x, y + height - radius);
    context.lineTo(x, y + radius);
    context.quadraticCurveTo(x, y, x + radius, y);
    context.closePath();
    context.fill();
  }

  /**
   * Stroke a rounded rectangle
   */
  private strokeRoundedRect(
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ): void {
    context.beginPath();
    context.moveTo(x + radius, y);
    context.lineTo(x + width - radius, y);
    context.quadraticCurveTo(x + width, y, x + width, y + radius);
    context.lineTo(x + width, y + height - radius);
    context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    context.lineTo(x + radius, y + height);
    context.quadraticCurveTo(x, y + height, x, y + height - radius);
    context.lineTo(x, y + radius);
    context.quadraticCurveTo(x, y, x + radius, y);
    context.closePath();
    context.stroke();
  }

  /**
   * Get paddle bounds for collision detection
   */
  public getBounds(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.props.paddle.position.x,
      y: this.props.paddle.position.y,
      width: this.props.paddle.size.width,
      height: this.props.paddle.size.height
    };
  }

  /**
   * Check if point is inside paddle
   */
  public containsPoint(x: number, y: number): boolean {
    const bounds = this.getBounds();
    return x >= bounds.x && 
           x <= bounds.x + bounds.width &&
           y >= bounds.y && 
           y <= bounds.y + bounds.height;
  }

  /**
   * Get paddle center point
   */
  public getCenter(): { x: number; y: number } {
    return {
      x: this.props.paddle.position.x + this.props.paddle.size.width / 2,
      y: this.props.paddle.position.y + this.props.paddle.size.height / 2
    };
  }
}

/**
 * Factory function to create Paddle instances
 */
export function createPaddle(props: PaddleProps): Paddle {
  return new Paddle(props);
}

/**
 * Static rendering function for functional-style usage
 */
export function renderPaddle(props: PaddleProps): void {
  const paddle = new Paddle(props);
  paddle.render();
} 